import {APIProvider} from '@vis.gl/react-google-maps';
import {Alert} from 'platform/components';
import {Box, Heading, Show, VStack} from 'platform/foundation';

import {ReactNode, useState} from 'react';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {TestDriveRouteMapContent} from './components/TestDriveRouteMapContent';

type TestDriveRouteMapProps = TestIdProps & {
  /** Google encoded polyline recorded by the CarAudit app during the test drive. */
  encodedRoute: string | Nullish;
  isTestDriveRecorded: boolean;
  /** Distance and duration of the drive, shown over the map. */
  summary?: string | Nullish;
  /** Rendered when the map cannot be shown, so the place of the section is not left empty. */
  fallback?: ReactNode;
};

export function TestDriveRouteMap(props: TestDriveRouteMapProps) {
  const [hasLoadError, setHasLoadError] = useState(false);
  const googleApiKey = process.env.GOOGLE_API_KEY ?? '';

  const hasRoute = isNotNilOrEmpty(props.encodedRoute);
  const isMapAvailable = isNotNilOrEmpty(googleApiKey) && !hasLoadError;
  const mapRoute = hasRoute && isMapAvailable ? props.encodedRoute : null;

  // Without a route, only a recorded test drive explains the missing map.
  // With a route that the map cannot show, the fallback takes the section's place.
  if (hasRoute ? !isMapAvailable : !props.isTestDriveRecorded) {
    return <>{props.fallback}</>;
  }

  return (
    <>
      <Show when={mapRoute}>
        {(encodedRoute) => (
          // The map fills the column, so it lines up with the test drive attributes beside it.
          <Box height="100%" data-testid={props['data-testid']}>
            <APIProvider apiKey={googleApiKey} onError={() => setHasLoadError(true)}>
              <TestDriveRouteMapContent
                encodedRoute={encodedRoute}
                summary={props.summary}
                data-testid={suffixTestId('map', props)}
              />
            </APIProvider>
          </Box>
        )}
      </Show>
      <Show when={!hasRoute}>
        <VStack spacing={3} data-testid={props['data-testid']}>
          <Heading size={4}>{i18n.t('entity.condition.labels.testDriveRoute')}</Heading>
          <Alert
            type="inline"
            variant="info"
            message={i18n.t('entity.condition.notifications.testDriveRouteUnavailable')}
            data-testid={suffixTestId('routeUnavailable', props)}
          />
        </VStack>
      </Show>
    </>
  );
}
